import { request, type RequestOptions } from "./client";
import type {
  AdminDashboardResponse,
  AdminJobDetail,
  AdminJobListResponse,
  AdminLoginResponse,
  AdminSession,
  AdminSettingsPatch,
  AdminSettingsResponse,
  AdminUsersResponse,
  CreateJobRequest,
  CreateJobResponse,
  JobStatusResponse,
  PlaylistSummary,
} from "./types";

function adminRequest<T>(path: string, options: RequestOptions = {}) {
  return request<T>(path, { ...options, credentials: "include" });
}

export function adminLogin(password: string): Promise<AdminLoginResponse> {
  return adminRequest<AdminLoginResponse>("/api/admin/login", {
    method: "POST",
    json: { password },
  });
}

export function adminLogout(): Promise<void> {
  return adminRequest<void>("/api/admin/logout", { method: "POST" });
}

export function getAdminSession(): Promise<AdminSession> {
  return adminRequest<AdminSession>("/api/admin/session");
}

export function getAdminDashboard(): Promise<AdminDashboardResponse> {
  return adminRequest<AdminDashboardResponse>("/api/admin/dashboard");
}

export function getAdminUsers(): Promise<AdminUsersResponse> {
  return adminRequest<AdminUsersResponse>("/api/admin/users");
}

export function listAdminJobs(params: {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<AdminJobListResponse> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") query.set(key, String(value));
  }
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return adminRequest<AdminJobListResponse>(`/api/admin/jobs${suffix}`);
}

export function getAdminJob(jobId: string): Promise<AdminJobDetail> {
  return adminRequest<AdminJobDetail>(`/api/admin/jobs/${jobId}`);
}

export function createAdminJob(
  payload: CreateJobRequest,
): Promise<CreateJobResponse> {
  return adminRequest<CreateJobResponse>("/api/admin/jobs", {
    method: "POST",
    json: payload,
  });
}

export function cancelAdminJob(jobId: string): Promise<JobStatusResponse> {
  return adminRequest<JobStatusResponse>(`/api/admin/jobs/${jobId}/cancel`, {
    method: "POST",
  });
}

export function deleteAdminJob(jobId: string): Promise<void> {
  return adminRequest<void>(`/api/admin/jobs/${jobId}`, {
    method: "DELETE",
  });
}

export function addAdminSongToPlaylist(
  playlistSlug: string,
  jobId: string,
): Promise<PlaylistSummary> {
  return adminRequest<PlaylistSummary>(
    `/api/admin/playlists/${playlistSlug}/songs/${jobId}`,
    { method: "POST" },
  );
}

export function removeAdminSongFromPlaylist(
  playlistSlug: string,
  jobId: string,
): Promise<void> {
  return adminRequest<void>(
    `/api/admin/playlists/${playlistSlug}/songs/${jobId}`,
    { method: "DELETE" },
  );
}

export function getAdminSettings(): Promise<AdminSettingsResponse> {
  return adminRequest<AdminSettingsResponse>("/api/admin/settings");
}

export function patchAdminSettings(
  payload: AdminSettingsPatch,
): Promise<AdminSettingsResponse> {
  return adminRequest<AdminSettingsResponse>("/api/admin/settings", {
    method: "PATCH",
    json: payload,
  });
}
